if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.thumbnailNavigationMode = this.dataset.thumbnailNavigationMode || 'arrows';
        if (!this.elements.thumbnails) return;

        this.thumbnailTrack = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.thumbnailNavigationMode === 'swipe_drag' && this.thumbnailTrack) {
          this.initDesktopThumbnailDrag();
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initDesktopThumbnailDrag() {
        this.thumbnailDragState = {
          pointerDown: false,
          hasDragged: false,
          startX: 0,
          startScrollLeft: 0,
          suppressClick: false,
        };

        this.thumbnailTrack.addEventListener('pointerdown', this.onThumbnailPointerDown.bind(this));
        this.thumbnailTrack.addEventListener('pointermove', this.onThumbnailPointerMove.bind(this));
        this.thumbnailTrack.addEventListener('pointerup', this.onThumbnailPointerUp.bind(this));
        this.thumbnailTrack.addEventListener('pointercancel', this.onThumbnailPointerUp.bind(this));
        this.thumbnailTrack.addEventListener(
          'click',
          (event) => {
            if (!this.thumbnailDragState.suppressClick) return;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.thumbnailDragState.suppressClick = false;
          },
          true
        );
      }

      onThumbnailPointerDown(event) {
        if (!this.mql.matches || event.pointerType !== 'mouse' || event.button !== 0) return;

        this.thumbnailDragState.pointerDown = true;
        this.thumbnailDragState.hasDragged = false;
        this.thumbnailDragState.startX = event.clientX;
        this.thumbnailDragState.startScrollLeft = this.thumbnailTrack.scrollLeft;
      }

      onThumbnailPointerMove(event) {
        if (!this.thumbnailDragState.pointerDown || !this.mql.matches) return;

        const deltaX = event.clientX - this.thumbnailDragState.startX;
        if (!this.thumbnailDragState.hasDragged && Math.abs(deltaX) > 6) {
          this.thumbnailDragState.hasDragged = true;
          this.thumbnailTrack.classList.add('is-dragging');
        }

        if (!this.thumbnailDragState.hasDragged) return;

        event.preventDefault();
        this.thumbnailTrack.scrollLeft = this.thumbnailDragState.startScrollLeft - deltaX;
      }

      onThumbnailPointerUp() {
        if (!this.thumbnailDragState.pointerDown) return;

        this.thumbnailDragState.pointerDown = false;
        this.thumbnailTrack.classList.remove('is-dragging');

        if (!this.thumbnailDragState.hasDragged) return;

        this.thumbnailDragState.suppressClick = true;
        window.setTimeout(() => {
          this.thumbnailDragState.suppressClick = false;
        }, 250);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}